/**
 * Client / Entity / Position column filters for the server-side Documents grid.
 *
 * Modeled directly on `fund-admin-frontend`'s `data-vault/entities` grid, which
 * is the reference implementation for AG-Grid-integrated client/entity
 * selectors: its `arena` column takes `ClientFilter` and its `name` column takes
 * `EntityFilter` with `filterParams: { dependentFilter: "arena", multiselect:
 * true }`, so picking a client scopes what the entity search offers.
 *
 * WHY THESE EXIST RATHER THAN A STOCK CONTROL
 * ---------------------------------------------------------------------------
 * Client and Entity are `reference` attributes. The Vault stores a reference
 * value as an opaque id in `document_attribute_values.value_id`, and the
 * backend allows exactly three operators on that column — `any_of`, `blank`,
 * `not_blank` (`ALLOWED_OPERATORS_BY_VALUE_TYPE`). A text box therefore cannot
 * filter one: the string a user types is a NAME, which is not what is stored,
 * and `equals` is rejected outright rather than merely returning nothing. A set
 * filter can't either — there is no option table for a reference, so there is no
 * list to render. The only workable control is a typeahead over the source of
 * truth that yields ids, which is exactly what `ClientSearch` / `EntitySearch`
 * are.
 *
 * WHY THE MODEL CARRIES BOTH ID FORMS
 * ---------------------------------------------------------------------------
 * The search components report relay GlobalIDs; `value_id` holds bare pks (the
 * write path decodes, the search path does not — see `#common/globalId`). So the
 * model keeps `ids` (bare, for the wire) alongside `globalIds` (what
 * `EntitySearch` needs to scope its query, and what re-renders the chips).
 *
 * These mount `@jsq/common-components` search components (or the in-repo
 * `PositionSearch`), which run their own GraphQL queries — so they only work
 * under an `ApolloProvider`, i.e. on the connected server grid. The
 * presentational client-side `DocumentBrowser` keeps its row-derived set filter.
 */
import React, { useCallback, useEffect, useState } from "react";
import { ClientSearch, EntitySearch } from "@jsq/common-components";
import { AgGridReact } from "@jsq/ds/core";
import { toBareReferenceId } from "#common/globalId";
import {
  REFERENCE_FILTER_TYPE,
  type VaultReferenceFilterModel,
} from "#common/graphql/Document";
import { PositionSearch } from "#components/Search/PositionSearch";

/** Build the filter model from the GlobalIDs a search component reported. */
const modelFor = (globalIds: string[]): VaultReferenceFilterModel | null =>
  globalIds.length
    ? {
        filterType: REFERENCE_FILTER_TYPE,
        ids: globalIds.map(toBareReferenceId),
        globalIds,
      }
    : null;

const asModel = (model: unknown): VaultReferenceFilterModel | null => {
  if (typeof model !== "object" || model === null) return null;
  const candidate = model as Partial<VaultReferenceFilterModel>;
  return Array.isArray(candidate.ids) ? (candidate as VaultReferenceFilterModel) : null;
};

/** The GlobalIDs currently picked in a reference filter model. */
export const pickedGlobalIds = (model: unknown): string[] => asModel(model)?.globalIds ?? [];

/**
 * `doesFilterPass` is only ever called by the CLIENT-side row model. These
 * filters are mounted on the server-side grid, where the predicate is evaluated
 * by `vaultDocumentsSearch`, so every loaded row has already passed. Returning
 * `false` here would blank a correctly-filtered grid.
 */
const alwaysPasses = () => true;

export type VaultReferenceFilterProps = Omit<AgGridReact.CustomFilterProps, "model"> & {
  /**
   * AG Grid types the model as `any`. Narrowed to `unknown` so every read has to
   * go through {@link pickedGlobalIds}, which is the only thing that knows what
   * shape a reference filter's model has.
   */
  model: unknown;
  /**
   * Column id of the Client filter this one depends on. Set on the Entity
   * filter; the client search that scopes it reads that column's model.
   */
  dependentFilter?: string;
};

/** Client (arena) filter — a multi-select typeahead over main's arenas. */
export const VaultClientFilter = (props: VaultReferenceFilterProps) => {
  const { onModelChange, model } = props;
  const values = pickedGlobalIds(model);

  const handleMultiSelect = useCallback(
    (globalIds: string[]) => onModelChange(modelFor(globalIds)),
    [onModelChange],
  );

  AgGridReact.useGridFilter({ doesFilterPass: alwaysPasses });

  return (
    <ClientSearch
      multiselect
      values={values}
      placeholder="Search for a client"
      testId="vault-client-filter"
      // `multiselect` routes selections through `onMultiSelect`; `onSelect` is
      // required by the prop type and never fires in this mode.
      onSelect={(globalId) => handleMultiSelect(globalId ? [globalId] : [])}
      onMultiSelect={handleMultiSelect}
      onSearchClear={() => onModelChange(null)}
    />
  );
};

/**
 * The GlobalIDs currently picked in a reference column filter, kept in sync as
 * that filter changes.
 *
 * This has to SUBSCRIBE rather than just read `getColumnFilterModel` during
 * render. Picking a client changes the Client filter's model — not this
 * component's props — so React has no reason to re-render the Entity or Position
 * filter, and AG Grid keeps all of them mounted at once in the Filters tool
 * panel. Read-at-render therefore leaves a dependent search stuck on "Please
 * select …" forever, which is precisely what it did.
 *
 * `filterChanged` is the grid-level event, so it fires for any filter's change
 * even though this component owns a different column.
 */
function useDependentIds(
  api: AgGridReact.CustomFilterProps["api"],
  dependentFilter: string | undefined,
): string[] {
  const read = useCallback(
    () => (dependentFilter ? pickedGlobalIds(api.getColumnFilterModel(dependentFilter)) : []),
    [api, dependentFilter],
  );
  const [ids, setIds] = useState<string[]>(read);

  useEffect(() => {
    if (!dependentFilter) return undefined;
    const sync = () =>
      // Replace only on a real change: `filterChanged` also fires for unrelated
      // columns, and a fresh array every time would re-render (and re-query)
      // the dependent search for each of them.
      setIds((previous) => {
        const next = read();
        return next.join(",") === previous.join(",") ? previous : next;
      });
    sync();
    api.addEventListener("filterChanged", sync);
    return () => {
      // The grid can be torn down before this filter unmounts (a scope change
      // re-keys it), and calling into a destroyed api only logs a warning.
      try {
        api.removeEventListener("filterChanged", sync);
      } catch {
        /* grid destroyed */
      }
    };
  }, [api, dependentFilter, read]);

  return ids;
}

/**
 * Entity filter — scoped by whatever the Client filter holds.
 *
 * With no client picked, `EntitySearch` renders its own "please select the
 * client first" state rather than searching every entity in the firm; that is
 * the same cascade `data-vault/entities` uses, and it is also a practical
 * necessity, since main rejects an entity query that names no arena.
 */
export const VaultEntityFilter = (props: VaultReferenceFilterProps) => {
  const { onModelChange, model, dependentFilter, api } = props;
  const values = pickedGlobalIds(model);
  const clientIds = useDependentIds(api, dependentFilter);

  const handleMultiSelect = useCallback(
    (globalIds: string[]) => onModelChange(modelFor(globalIds)),
    [onModelChange],
  );

  AgGridReact.useGridFilter({ doesFilterPass: alwaysPasses });

  return (
    <EntitySearch
      clientIds={clientIds}
      multiselect
      values={values}
      placeholder="Search for an entity"
      testId="vault-entity-filter"
      onSelect={(globalId) => handleMultiSelect(globalId ? [globalId] : [])}
      onMultiSelect={handleMultiSelect}
      onSearchClear={() => onModelChange(null)}
    />
  );
};

type VaultPositionFilterProps = VaultReferenceFilterProps & {
  /** Column id of the Client (arena) filter that scopes the position search. */
  dependentArenaFilter: string;
  /** Column id of the Entity filter that scopes the position search. */
  dependentEntityFilter: string;
};

/**
 * Position (LP/Investor) filter — a multi-select typeahead over investor
 * positions, scoped by the Client and Entity filters already on the grid.
 *
 * Both upstream models are read via subscriptions so picking a client or entity
 * while the Filters panel is open immediately re-scopes this search — the same
 * pattern `VaultEntityFilter` uses for its client dependency.
 */
export const VaultPositionFilter = (props: VaultPositionFilterProps) => {
  const { onModelChange, model, dependentArenaFilter, dependentEntityFilter, api } = props;
  const values = pickedGlobalIds(model);
  const arenaIds = useDependentIds(api, dependentArenaFilter);
  const entityIds = useDependentIds(api, dependentEntityFilter);

  const handleMultiSelect = useCallback(
    (globalIds: string[]) => onModelChange(modelFor(globalIds)),
    [onModelChange],
  );

  AgGridReact.useGridFilter({ doesFilterPass: alwaysPasses });

  return (
    <PositionSearch
      arenaIds={arenaIds}
      entityIds={entityIds}
      multiselect
      values={values}
      placeholder="Search for a position"
      testId="vault-position-filter"
      onSelect={(globalId) => handleMultiSelect(globalId ? [globalId] : [])}
      onMultiSelect={handleMultiSelect}
      onSearchClear={() => onModelChange(null)}
    />
  );
};

/**
 * The slice of `FilterChangedEvent` {@link clearDependentReferenceFilters} reads.
 * Structural rather than the AG Grid type so the helper stays testable without
 * standing up a grid — a real `FilterChangedEvent` satisfies it.
 */
export type FilterChangedLike = {
  columns?: { getColId: () => string }[] | null;
  api: {
    getFilterModel: () => Record<string, unknown>;
    setFilterModel: (model: Record<string, unknown>) => void;
  };
};

/**
 * Drop downstream reference filters when an upstream filter changes.
 *
 * - Client changed → clear entity AND investor (an entity and a position both
 *   belong to exactly one client; a stranded selection produces an empty result
 *   that reads as "broken").
 * - Entity changed → clear investor (a position belongs to exactly one entity).
 *
 * Mirrors the `onFilterChanged` handler on `data-vault/entities`' grid.
 */
export const clearDependentReferenceFilters = (
  event: FilterChangedLike,
  {
    clientColId,
    entityColId,
    investorColId,
  }: { clientColId?: string; entityColId?: string; investorColId?: string },
): void => {
  const changedCols = event.columns ?? [];
  const clientChanged =
    !!clientColId && changedCols.some((col) => col.getColId() === clientColId);
  const entityChanged =
    !!entityColId && changedCols.some((col) => col.getColId() === entityColId);

  if (!clientChanged && !entityChanged) return;

  const current = event.api.getFilterModel();
  const next: Record<string, unknown> = { ...current };
  let dirty = false;

  if (clientChanged && entityColId && current[entityColId] != null) {
    next[entityColId] = null;
    dirty = true;
  }
  if ((clientChanged || entityChanged) && investorColId && current[investorColId] != null) {
    next[investorColId] = null;
    dirty = true;
  }

  if (dirty) event.api.setFilterModel(next);
};
